export type CollectionKey = string | number | symbol;

export type CollectionIterateFn<Key, Value> = (value: Value, key: Key) => void;

/**
 * Test if a value is an immutable collection.
 */
export function isCollection(maybeCollection: any, type?: symbol): maybeCollection is Collection {
    if (!maybeCollection) {
        return false;
    }
    const symbol = maybeCollection[COLLECTION_TYPE_SYMBOL];
    return type ? symbol === type : Boolean(symbol);
}

/**
 * Test if 2 objects are immutable collections of the same type.
 */
export function isSameCollectionType<T extends Collection>(
    collectionA: T,
    collectionB: any
): collectionB is T {
    if (!isCollection(collectionA) || !isCollection(collectionB)) {
        return false;
    }

    // @ts-ignore
    return collectionA[COLLECTION_TYPE_SYMBOL] === collectionB[COLLECTION_TYPE_SYMBOL];
}

/**
 * Key to store on the prototype of a collection to identify it as a collection.
 */
export const COLLECTION_TYPE_SYMBOL = Symbol('@@__IMMUTABLE_COLLECTION__@@');

export type CollectionEntry<Key, Value> = [Key, Value];

/**
 * Class implemented by all collections.
 * Classes dont' extend this class directly but instead implements it to ensure
 * high performance.
 */
export abstract class Collection<Key extends CollectionKey = any, Value = any> {
    public static isKeyed: boolean;
    public static isOrdered: boolean;

    /**
     * Returns the number of items in this collection.
     */
    public abstract readonly size: number;

    /**
     * Returns the hashCode for this collection.
     * This method is implemented for compatibility with Immutable.js.
     */
    public abstract hashCode(): number;

    /**
     * Get the value for a given index. This method is only available for ordered collections.
     */
    public abstract getByIndex(index: number): CollectionEntry<Key, Value> | undefined;

    /**
     * Get the value for a given key. This method is only available for keyed collections.
     */
    public abstract getByKey(key: Key): CollectionEntry<Key, Value> | undefined;

    /**
     * Iterate through all entries in the collection.
     */
    public abstract iterate(fn: CollectionIterateFn<Key, Value>): void;

    /**
     * Return all the keys in this collection as an array.
     */
    public abstract toKeysArray(): Array<Key>;

    /**
     * Return all the values in this collection as an array.
     */
    public abstract toValuesArray(): Array<Value>;

    /**
     * Return all the entries in this collection as an array.
     */
    public abstract toEntriesArray(): Array<[Key, Value]>;

    /**
     * Test if the collection is equal to another collection of the same type.
     */
    public abstract equals(other: any): boolean;
}

/**
 * Stringify a collection as a string.
 */
export function stringifyCollection(collection: any | Collection, indent = ''): string {
    if (!(collection instanceof Collection)) {
        return JSON.stringify(collection);
    }

    let result = `${collection.constructor.name} (${collection.size}) `;

    if (collection.size === 0) {
        return `${result}{}`;
    }
    result += '{\n';

    const propIndent = `${indent}  `;
    collection.iterate((v, k) => {
        result += `${propIndent}${k}: ${stringifyCollection(v, propIndent)}\n`;
    });

    result += `${indent}}`;

    return result;
}
